import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { Partners } from './components/Partners';
import { Milestones } from './components/Milestones';
import { SpecializedOfferings } from './components/SpecializedOfferings';
import { ContactForm } from './components/ContactForm';
import { Footer } from './components/Footer';
import { LeadCaptureModal } from './components/LeadCaptureModal';
import { ProductDetail } from './components/ProductDetail';
import { SEOHead } from './components/SEOHead';
import { NotFound } from './components/NotFound';
import ClientsJourneyPage from './clients-journey/page';
import { Toaster } from 'sonner';

// Import logos for product mapping
import logoTranslation from "../assets/logos/Anuvadini_Translation.png";
import logoPrint from "../assets/logos/Anuvadini_Print.png";
import logoGeoAi from "../assets/logos/Anuvadini-GEO-AI.png";
import logoAgents from "../assets/logos/Anuvadini_AI_Agents.png";
import logoConnect from "../assets/logos/Anuvadini_Connect.png";
import logoDevelop from "../assets/logos/Anuvadini_Develop.png";
import logoVoice from '../assets/logos/Anuvadini Voice, Audio & Video.png';
import logoGames from "../assets/logos/Anuvadini Games.png";
import logoDrafting from "../assets/logos/Anuvadini Drafting & OCR.png";

// Helper to find product by slug or title
const getProductBySlug = (slug: string) => {
  // Simple mapping based on known products - using exact colors from serviceCards
  const products: Record<string, any> = {
    'anuvadini-translation': {
      title: "Anuvadini Translation",
      desc: "Create and translate content across 22 Indian and 89+ global languages using our in-house LLM and expert linguists.",
      logo: logoTranslation,
      gradient: "from-[#4f46e5] via-[#6366f1] to-[#4f46e5]",
      buttonBg: "bg-[#c2410c]",
      shadow: "shadow-orange-200"
    },
    'anuvadini-print': {
      title: "Anuvadini Print",
      desc: "Design, format, and publish professional books, research papers, and novels without complex software.",
      logo: logoPrint,
      gradient: "from-[#0ea5e9] via-[#38bdf8] to-[#22d3ee]",
      buttonBg: "bg-[#3730a3]",
      shadow: "shadow-indigo-200"
    },
    'anuvadini-geo-ai': {
      title: "Anuvadini GEO-AI",
      desc: "Unlock precise insights across coastlines, rivers, forests, and agriculture. Map urban assets, land use, and infrastructure.",
      logo: logoGeoAi,
      gradient: "from-[#a855f7] via-[#c084fc] to-[#8b5cf6]",
      buttonBg: "bg-[#9d174d]",
      shadow: "shadow-rose-200"
    },
    'anuvadini-ai-agents': {
      title: "Anuvadini AI Agents",
      desc: "Develop intelligent multilingual AI agents trained on websites, PDFs, and domain knowledge.",
      logo: logoAgents,
      gradient: 'from-[#008080] via-[#008080] to-[#008080]',
      buttonBg: 'bg-[#6d28d9]',
      shadow: 'shadow-purple-200',
    },
    'anuvadini-bharatt': {
      title: "Anuvadini Bharatt",
      desc: "Make-in-India and Aatmanirbhar Bharat solutions for secure, intelligent, multilingual communication.",
      logo: logoConnect,
      gradient: 'from-[#87CEEB] via-[#87CEEB] to-[#87CEEB]',
      buttonBg: 'bg-[#0284c7]',
      shadow: 'shadow-sky-200',
      list: [
        {
          label: 'Hyped Samvadini Super App - Secure, seamless multilingual messaging',
          href: 'https://hyped.aicte-india.org/',
        },
        {
          label: 'Desi AI GenZ Keyboard - Type, speak, and translate in 22 Indian and 57+ global languages',
          href: 'https://desi.aicte-india.org/',
        },
        {
          label: 'Anuvadini Bharatt - AI-powered video conferencing for teams and webinars',
          href: 'https://anuvadinimeet.aicte-india.org/',
        }
      ]
    },
    'anuvadini-web-mobile': {
      title: "Anuvadini Web & Mobile",
      desc: "75+ customized solutions across supply chain, banking, education, legal systems, and real-time workflow tracking. Built for zettabytes of data, multilingual engagement, and sustainable growth.",
      logo: logoDevelop,
      gradient: 'from-[#BF868F] via-[#BF868F] to-[#BF868F]',
      buttonBg: 'bg-[#0f766e]',
      shadow: 'shadow-emerald-200',
      biometricApp: {
        title: "Anuvadini Biometric App",
        description: "The Anuvadini Biometric App is a secure, AI-powered attendance and identity verification solution that enables organizations to record attendance seamlessly using advanced biometric authentication methods. Designed for reliability and transparency, the app supports multiple biometric modalities including Face Recognition, Voice Authentication, Iris Scanning, and Palm Recognition, ensuring accurate identity validation.",
        geoFencing: "To maintain authenticity and prevent misuse, attendance can only be marked within the predefined geo-fenced location range of the organization. This ensures that employees, students, or staff can record attendance only when physically present at authorized premises.",
        dashboard: "The app also provides a comprehensive real-time dashboard that allows users and administrators to monitor attendance history, track trends, generate reports, and analyze workforce or institutional presence with actionable insights.",
        capabilities: [
          "Multi-Modal Biometric Authentication – Face, Voice, Iris, and Palm-based attendance marking.",
          "Geo-Fenced Attendance Control – Attendance allowed only within authorized location boundaries.",
          "Real-Time Attendance Dashboard – View attendance records, trends, and analytics instantly.",
          "Secure & Tamper-Resistant System – Prevents proxy attendance and unauthorized access.",
          "Role-Based Access Control – Separate views for administrators, managers, and users.",
          "Analytics & Reporting – Detailed insights for compliance, workforce management, and performance tracking."
        ],
        bestFor: "Government offices, educational institutions, corporate enterprises, factories, field workforce management, training centers, and organizations requiring secure, location-verified attendance systems."
      }
    },
    'anuvadini-voice-audio-video': {
      title: "Anuvadini Voice, Audio & Video",
      desc: "Deliver accurate lip-syncing, real-time translation, lifelike voice cloning, and audio modulation with natural results.",
      logo: logoVoice,
      gradient: "from-[#f97316] via-[#db2777] to-[#4f46e5]", // Keep existing Voice colors
      buttonBg: "bg-[#c2410c]",
      shadow: "shadow-orange-200"
    },
    'anuvadini-games': {
      title: "Anuvadini Games",
      desc: "Interactive learning and engagement games including quizzes, multiplayer challenges, and classics like Snake & Ladder.",
      logo: logoGames,
      gradient: "from-[#db2777] via-[#ec4899] to-[#db2777]", // Keep existing Games colors
      buttonBg: "bg-[#9d174d]",
      shadow: "shadow-rose-200"
    },
    'anuvadini-drafting-ocr': {
      title: "Anuvadini Drafting & OCR",
      desc: "End-to-end document formatting and digitization with industry-grade tooling. OCR for handwritten and legacy documents.",
      logo: logoDrafting,
      gradient: "from-[#f59e0b] via-[#d97706] to-[#b45309]",
      buttonBg: "bg-[#92400e]",
      shadow: "shadow-amber-200"
    }
  };
  return products[slug];
};

const ProductDetailWrapper = ({ onContact }: { onContact: () => void }) => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const product = slug ? getProductBySlug(slug) : null;

  useEffect(() => {
    if (slug && !product) {
      navigate('/');
    }
  }, [slug, product, navigate]);

  if (!product) return null;

  // Generate SEO data for product page
  const productSEO = {
    title: `${product.title} | Anuvadini AI Platform`,
    description: `${product.desc} - Part of Anuvadini AI's comprehensive multilingual platform suite.`,
    keywords: `${product.title}, Anuvadini AI, AI translation, multilingual platform, ${slug}`,
    url: `https://anuvadini.ai/product/${slug}`,
    image: product.logo || "https://anuvadini.ai/anuvadini-social-preview.jpg"
  };

  const handleNavigateToSpecialized = () => {
    navigate('/#specialized-offerings');
  };

  return (
    <>
      <SEOHead {...productSEO} />
      <ProductDetail
        product={product}
        onBack={() => navigate('/')}
        onContact={onContact}
      />
      <Footer onRequestDemo={onContact} onNavigateToSpecialized={handleNavigateToSpecialized} />
    </>
  );
};

function App() {
  const [isLeadOpen, setIsLeadOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpenLead = () => setIsLeadOpen(true);
  const handleCloseLead = () => setIsLeadOpen(false);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);

  const handleShowProductDetail = (product: any, sectionId?: string) => {
    // Map title to slug
    const slug = product.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    if (sectionId) {
      navigate(`/product/${slug}#${sectionId}`);
    } else {
      navigate(`/product/${slug}`);
    }
  };

  const handleNavigateToSpecialized = () => {
    const element = document.getElementById('specialized-offerings');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 selection:bg-[#f97316]/20 selection:text-[#f97316]">
      <Toaster position="top-center" richColors theme="light" />

      {/* Always show navbar */}
      <Navbar onRequestDemo={handleOpenLead} />

      <Routes>
        <Route path="/" element={
          <>
            <SEOHead />
            <main>
              <Hero
                onContact={handleOpenLead}
                onShowProductDetail={handleShowProductDetail}
              />

              <Partners />

              {/* Subtle decorative separator */}
              <div className="max-w-7xl mx-auto px-4 py-16">
                <div className="h-px w-full bg-gradient-to-r from-transparent via-[#4f46e5]/10 to-transparent" />
              </div>

              <Milestones />
              <SpecializedOfferings
                onContact={handleOpenLead}
                onShowProductDetail={handleShowProductDetail}
              />

              {/* Secondary CTA Section */}
              <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                  <div className="bg-gradient-to-r from-orange-600 to-rose-600 rounded-[3rem] p-12 md:p-20 text-center relative overflow-hidden group">
                    <div className="absolute top-0 left-0 w-full h-full bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative z-10">
                      <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 text-justify-inter-word">Ready to scale multilingual intelligence?</h2>
                      <p className="text-orange-50 text-xl mb-10 max-w-2xl text-justify-inter-word">
                        Let us build a secure, future-ready AI platform for your organization today.
                      </p>
                      <button
                        type="button"
                        onClick={handleOpenLead}
                        className="bg-green-400 hover:bg-green-500 text-white px-10 py-4 rounded-2xl font-bold text-lg shadow-xl hover:scale-105 transition-all"
                      >
                        Connect with Anuvadini
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              <ContactForm />
            </main>

            <Footer onRequestDemo={handleOpenLead} onNavigateToSpecialized={handleNavigateToSpecialized} />
          </>
        } />
        <Route path="/product/:slug" element={<ProductDetailWrapper onContact={handleOpenLead} />} />
        <Route path="/clients-journey" element={<ClientsJourneyPage onContact={handleOpenLead} />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* Lead capture modal should be available on both pages */}
      <LeadCaptureModal open={isLeadOpen} onClose={handleCloseLead} />
    </div>
  );
}

export default App;
